import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Feedback } from 'src/app/decorators/feedback-decorator';
import { CustomersStateService } from 'src/app/state/customers.state.service';
import { BulkCreateCustomerRequest } from '@me-fit-mono/typings';
import { phoneNumberValidatorPattern } from 'src/app/utils/phone-number-pattern';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'me-bulk-customer-create',
  templateUrl: './bulk-customer-create.component.html',
  styles: [
    `:host {
      display: block;
    }`
  ]
})
export class BulkCustomerCreateComponent implements OnInit {

  form: FormGroup;

  @Input({required: false}) payload?: BulkCreateCustomerRequest;

  @Input() bulkCreateButtonText = this.$translate.instant('customers.bulk.create')

  @Output() onCustomersCreated = new EventEmitter<void>();

  constructor(
    private fb: FormBuilder,
    private $customers: CustomersStateService,
    private $translate: TranslateService
  ) {
    this.form = this.fb.group({
      customers: this.fb.array([])
    });
  }

  ngOnInit() {

    if (this.payload) {
      this.populateFormWithData(this.payload);
    } else {
      this.addCustomer();
    }
  }

  get customersFormArray(): FormArray {
    return this.form.get('customers') as FormArray;
  }

  createCustomerForm(customerData?: BulkCreateCustomerRequest[number]): FormGroup {
    const customerForm = this.fb.group({
      firstName: [customerData?.firstName || '', [Validators.required, Validators.minLength(1)]],
      lastName: [customerData?.lastName || '', [Validators.required, Validators.minLength(1)]],
      phoneNumber: [customerData?.phoneNumber || '', [Validators.pattern(phoneNumberValidatorPattern)]],
      email: [customerData?.email || '', [Validators.email]],
      notes: this.fb.array([])
    });

    // Add notes if they exist
    if (customerData?.notes && customerData.notes.length > 0) {
      const notesArray = customerForm.get('notes') as FormArray;
      customerData.notes.forEach(note => {
        notesArray.push(this.getNoteGroup(note));
      });
    }

    return customerForm;
  }

  addCustomer(): void {
    this.customersFormArray.push(this.createCustomerForm());
  }

  removeCustomer(index: number): void {
    if (this.customersFormArray.length > 1) {
      this.customersFormArray.removeAt(index);
    }
  }

  getCustomerFormGroup(index: number): FormGroup {
    return this.customersFormArray.at(index) as FormGroup;
  }

  get isFormValid(): boolean {
    return this.form.valid && this.customersFormArray.length > 0;
  }

  getNoteGroup(note: BulkCreateCustomerRequest[number]['notes'][number]): FormGroup {
    return this.fb.group({
      text: [note.text, [Validators.required, Validators.minLength(1)]],
      createdAt: [note.createdAt, Validators.required],
      isEditable: [note.isEditable]
    });
  }

  hasValidData(): boolean {
    return this.customersFormArray.controls.some(control => {
      const customer = control.value;
      return customer.firstName?.trim() && customer.lastName?.trim();
    });
  }

  populateFormWithData(bulkCustomerData: BulkCreateCustomerRequest): void {
    // Clear existing form data
    this.customersFormArray.clear();

    // Populate form with provided data
    bulkCustomerData.forEach(customerData => {
      const customerForm = this.createCustomerForm(customerData);
      this.customersFormArray.push(customerForm);
    });

    // If no data was provided, add at least one empty customer form
    if (bulkCustomerData.length === 0) {
      this.addCustomer();
    }
  }

  @Feedback({
    i18nFailKey: 'customers.bulk.create.fail',
    i18nSuccessKey: 'customers.bulk.create.success',
  })
  async createCustomers() {
    if (!this.isFormValid) {
      return;
    }

    const customersData: BulkCreateCustomerRequest = this.customersFormArray.value.map((customer) => ({
      firstName: customer.firstName,
      lastName: customer.lastName,
      email: customer.email ? customer.email : undefined,
      phoneNumber: customer.phoneNumber?.trim() || undefined,
      notes: customer.notes.map((note: { text: string; isEditable: boolean; createdAt: string }) => ({
        text: note.text,
        isEditable: note.isEditable,
        createdAt: note.createdAt
      }))
    }));

    const result = await this.$customers.bulkCreate.call({
      request: customersData
    });

    if (result) {
      // Reset form after successful creation
      this.customersFormArray.clear();
      this.addCustomer();
      
      // Emit event when customers are successfully created
      this.onCustomersCreated.emit();
    }

    return result;
  }
}
